/* ============================================================
   <featured-product> custom element (Custom Product section)
   Features:
   - Section Rendering API variant updates (accurate price/inventory/sku/buy/pickup)
   - AJAX add to cart + toast
   - Image lightbox / zoom, gallery switching, mobile swipe
   - Sticky add-to-cart bar (IntersectionObserver)
   - Swatch UI, quantity stepper, tabs
   - Countdown, "people viewing", notify-me
   - Complementary + recently-viewed products (JSON endpoints + localStorage)
   - aria-live + reduced-motion aware
   ============================================================ */
(function () {
  if (customElements.get('featured-product')) return;

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function upscale(url, width) {
    if (!url) return url;
    if (/[?&]width=\d+/.test(url)) return url.replace(/([?&]width=)\d+/, '$1' + width);
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function formatMoney(cents) {
    try {
      if (window.Shopify && window.Shopify.formatMoney && window.theme && window.theme.moneyFormat) {
        return window.Shopify.formatMoney(cents, window.theme.moneyFormat);
      }
    } catch (e) {}
    var cur = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '';
    return (cur ? cur + ' ' : '$') + (cents / 100).toFixed(2);
  }

  function handleFromUrl(url) {
    if (!url) return '';
    var m = url.match(/\/products\/([^/?#]+)/);
    return m ? m[1] : '';
  }

  class FeaturedProduct extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.productUrl = (this.dataset.url || '').split('?')[0];
      this.variants = this.readJSON('[data-fp-variants]') || [];
      this.form = this.querySelector('.fp__form');
      this.idInput = this.querySelector('[data-fp-variant-id]');
      this.mainStage = this.querySelector('[data-fp-stage]');

      this.bindDelegation();
      this.initGallery();
      this.bindVariantInputs();
      this.bindAddToCart();
      this.observeSellingPlan();
      this.initStickyBar();
      this.initLightboxClose();
      this.initSwipe();
      this.initTabsDefault();
      this.initAnimations();
      this.initCountdowns();
      this.initViewing();
      this.initRecentlyViewed();
      this.initComplementary();
    }

    readJSON(sel) {
      var tag = this.querySelector(sel);
      if (!tag) return null;
      try { return JSON.parse(tag.textContent); } catch (e) { return null; }
    }

    /* ---------------- Event delegation ---------------- */
    bindDelegation() {
      this.addEventListener('click', (e) => {
        var t = e.target.closest('[data-fp-thumb],[data-fp-nav],[data-fp-qty],[data-fp-zoom],[data-fp-lightbox-close],[data-fp-share],[data-fp-share-copy],[data-fp-notify-toggle],[data-fp-notify-submit],[data-fp-sticky-add],[data-fp-tab]');
        if (!t) {
          if (e.target.classList && e.target.classList.contains('fp__lightbox')) this.closeLightbox();
          return;
        }
        if (t.hasAttribute('data-fp-thumb')) this.onThumb(t);
        else if (t.hasAttribute('data-fp-nav')) this.navigate(t.getAttribute('data-fp-nav'));
        else if (t.hasAttribute('data-fp-qty')) this.stepQty(t.getAttribute('data-fp-qty'));
        else if (t.hasAttribute('data-fp-zoom')) this.openLightbox(t);
        else if (t.hasAttribute('data-fp-lightbox-close')) this.closeLightbox();
        else if (t.hasAttribute('data-fp-share')) this.shareProduct(t);
        else if (t.hasAttribute('data-fp-share-copy')) this.copyLink(t);
        else if (t.hasAttribute('data-fp-notify-toggle')) this.toggleNotify();
        else if (t.hasAttribute('data-fp-notify-submit')) this.submitNotify();
        else if (t.hasAttribute('data-fp-sticky-add')) this.stickyAdd();
        else if (t.hasAttribute('data-fp-tab')) this.switchTab(t);
      });
    }

    /* ---------------- Gallery (sliding track) ---------------- */
    initGallery() {
      var slides = Array.prototype.slice.call(this.querySelectorAll('[data-fp-slide]'));
      var idx = slides.findIndex((s) => s.classList.contains('is-active'));
      if (idx < 0) idx = 0;
      var track = this.querySelector('[data-fp-track]');
      if (track) {
        var prev = track.style.transition;
        track.style.transition = 'none';
        this.slideTo(idx);
        void track.offsetHeight; // flush, avoid first-paint animation
        track.style.transition = prev;
      } else {
        this._slideIndex = idx;
      }
    }

    onThumb(btn) { this.showMedia(btn.dataset.mediaId); }

    showMedia(mediaId) {
      var slides = Array.prototype.slice.call(this.querySelectorAll('[data-fp-slide]'));
      var idx = slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
      if (idx === -1) idx = 0;
      this.slideTo(idx);
    }

    slideTo(idx) {
      var track = this.querySelector('[data-fp-track]');
      var slides = Array.prototype.slice.call(this.querySelectorAll('[data-fp-slide]'));
      if (!slides.length) return;
      idx = Math.max(0, Math.min(idx, slides.length - 1));
      this._slideIndex = idx;
      if (track) track.style.transform = 'translateX(' + (-idx * 100) + '%)';
      slides.forEach((s, i) => s.classList.toggle('is-active', i === idx));
      var activeId = slides[idx].dataset.mediaId;
      this.querySelectorAll('.fp__thumb').forEach((li) => {
        var b = li.querySelector('[data-fp-thumb]');
        li.classList.toggle('is-active', !!b && b.dataset.mediaId === String(activeId));
      });
    }

    navigate(dir) {
      var slides = this.querySelectorAll('[data-fp-slide]');
      if (!slides.length) return;
      var i = this._slideIndex || 0;
      i = dir === 'next' ? (i + 1) % slides.length : (i - 1 + slides.length) % slides.length;
      this.slideTo(i);
    }

    initSwipe() {
      if (this.dataset.enableSwipe !== 'true' || !this.mainStage) return;
      var x0 = null;
      this.mainStage.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
      this.mainStage.addEventListener('touchend', (e) => {
        if (x0 === null) return;
        var dx = e.changedTouches[0].clientX - x0;
        if (Math.abs(dx) > 40) this.navigate(dx < 0 ? 'next' : 'prev');
        x0 = null;
      }, { passive: true });
    }

    /* ---------------- Lightbox ---------------- */
    openLightbox(btn) {
      if (this.dataset.enableZoom !== 'true') return;
      var img = btn.querySelector('img');
      var box = this.querySelector('[data-fp-lightbox]');
      var lbImg = this.querySelector('[data-fp-lightbox-img]');
      if (!img || !box || !lbImg) return;
      lbImg.src = upscale(img.currentSrc || img.src, 2000);
      lbImg.alt = img.alt || '';
      box.hidden = false;
      box.setAttribute('aria-hidden', 'false');
      this._lastFocus = document.activeElement;
      this.querySelector('[data-fp-lightbox-close]').focus();
    }
    closeLightbox() {
      var box = this.querySelector('[data-fp-lightbox]');
      if (!box) return;
      box.hidden = true;
      box.setAttribute('aria-hidden', 'true');
      if (this._lastFocus) this._lastFocus.focus();
    }
    initLightboxClose() {
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.closeLightbox(); });
    }

    /* ---------------- Variants (Section Rendering API) ---------------- */
    bindVariantInputs() {
      this.addEventListener('change', (e) => {
        if (e.target.closest('[data-fp-option-input]') || (e.target.matches && e.target.matches('[data-fp-option-input]'))) {
          this.onVariantChange();
        }
      });
    }

    selectedOptions() {
      var picker = this.querySelector('[data-fp-variant-picker]');
      if (!picker) return null;
      var opts = [];
      picker.querySelectorAll('.fp__variant-option').forEach((el) => {
        var idx = parseInt(el.dataset.optionIndex, 10);
        var sel = el.querySelector('select[data-fp-option-input]');
        if (sel) opts[idx] = sel.value;
        else {
          var c = el.querySelector('input[data-fp-option-input]:checked');
          if (c) opts[idx] = c.value;
        }
      });
      return opts;
    }

    matchVariant() {
      var opts = this.selectedOptions();
      if (!opts) return this.variants[0];
      return this.variants.find((v) => v.options.length === opts.length && v.options.every((o, i) => o === opts[i]));
    }

    onVariantChange() {
      this.refreshSelectedSwatchStates();
      var variant = this.matchVariant();
      if (!variant) { this.setUnavailable(); return; }
      if (this.idInput) this.idInput.value = variant.id;
      this.updateUrl(variant.id);
      this.updateSelectedValueLabels();
      this.swapVariantImage(variant);
      this.fetchSection(variant.id);
    }

    refreshSelectedSwatchStates() {
      this.querySelectorAll('.fp__variant-pill, .fp__swatch').forEach((el) => {
        var input = el.querySelector('input');
        el.classList.toggle('is-selected', !!input && input.checked);
      });
    }

    updateSelectedValueLabels() {
      this.querySelectorAll('.fp__variant-option').forEach((el) => {
        var span = el.querySelector('[data-fp-selected-value]');
        if (!span) return;
        var sel = el.querySelector('select[data-fp-option-input]');
        if (sel) { span.textContent = sel.value; return; }
        var c = el.querySelector('input[data-fp-option-input]:checked');
        if (c) span.textContent = c.value;
      });
    }

    updateUrl(variantId) {
      if (!variantId) return;
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', url);
    }

    swapVariantImage(variant) {
      if (!variant.featured_media_id) return;
      this.showMedia(variant.featured_media_id);
    }

    fetchSection(variantId) {
      if (!this.productUrl) return;
      var url = this.productUrl + '?variant=' + variantId + '&section_id=' + this.sectionId;
      fetch(url)
        .then((r) => r.text())
        .then((html) => {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          // Keep the visitor's purchase option across the swap; the fresh
          // markup always arrives with the subscription pre-selected.
          var chosen = this.querySelector('.po__input:checked');
          var chosenSub = chosen ? !!chosen.value : true;
          ['[data-fp-price-block]', '[data-fp-inventory]', '[data-fp-sku]', '[data-fp-buy]', '[data-fp-pickup]', '[data-fp-plans]'].forEach((sel) => {
            var fresh = doc.querySelector(sel);
            var current = this.querySelector(sel);
            if (fresh && current) current.innerHTML = fresh.innerHTML;
          });
          this.querySelectorAll('[data-fp-plan]').forEach((plan) => {
            var input = plan.querySelector('.po__input');
            if (!input) return;
            input.checked = (!!input.value) === chosenSub;
            plan.classList.toggle('is-selected', input.checked);
          });
          // Sticky bar price + availability
          var freshSticky = doc.querySelector('.fp__sticky-price');
          var curSticky = this.querySelector('.fp__sticky-price');
          if (freshSticky && curSticky) curSticky.innerHTML = freshSticky.innerHTML;
          var addBtn = this.querySelector('[data-fp-add-btn]');
          var stickyAdd = this.querySelector('[data-fp-sticky-add]');
          if (addBtn && stickyAdd) { stickyAdd.disabled = addBtn.disabled; stickyAdd.textContent = addBtn.textContent.trim(); }
          this.announce(addBtn && addBtn.disabled ? 'Selected variant is unavailable' : 'Selection updated');
        })
        .catch(() => {});
    }

    setUnavailable() {
      var btn = this.querySelector('[data-fp-add-btn]');
      var txt = this.querySelector('[data-fp-add-text]');
      if (btn) btn.disabled = true;
      if (txt) txt.textContent = 'Unavailable';
    }

    /* ---------------- Quantity ---------------- */
    stepQty(dir) {
      var input = this.querySelector('[data-fp-qty-input]');
      if (!input) return;
      var min = parseInt(input.min, 10) || 1;
      var max = input.max ? parseInt(input.max, 10) : Infinity;
      var step = parseInt(input.step, 10) || 1;
      var val = (parseInt(input.value, 10) || min) + (dir === 'plus' ? step : -step);
      input.value = Math.max(min, Math.min(max, val));
    }

    /* ---------------- Add to cart (AJAX) ---------------- */
    bindAddToCart() {
      if (!this.form) return;
      this.form.addEventListener('submit', (e) => {
        var buy = this.querySelector('[data-fp-buy]');
        if (buy && buy.dataset.fpAjax === 'false') return; // native submit for subscription apps
        e.preventDefault();
        this.addToCart();
      });
    }

    addToCart() {
      this.stripForeignPlans();
      var btn = this.querySelector('[data-fp-add-btn]');
      var spinner = this.querySelector('[data-fp-spinner]');
      if (btn) { btn.classList.add('is-loading'); btn.disabled = true; }
      if (spinner) spinner.hidden = false;

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        body: new FormData(this.form)
      })
        .then((r) => r.json().then((data) => ({ ok: r.ok, data: data })))
        .then((res) => {
          if (!res.ok) throw new Error(res.data && res.data.description ? res.data.description : 'Could not add to cart');
          this.toast('Added to cart', this.productUrl ? null : null);
          this.refreshCart();
          document.dispatchEvent(new CustomEvent('cp:cart:added', { detail: { item: res.data } }));
        })
        .catch((err) => this.toast(err.message || 'Something went wrong'))
        .finally(() => {
          if (btn) { btn.classList.remove('is-loading'); btn.disabled = false; }
          if (spinner) spinner.hidden = true;
        });
    }

    refreshCart() {
      fetch('/cart.js').then((r) => r.json()).then((cart) => {
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { cart: cart } }));
        document.querySelectorAll('.cart-count-bubble, [data-cart-count]').forEach((el) => {
          var n = el.querySelector('span') || el;
          n.textContent = cart.item_count;
        });
      }).catch(() => {});
    }

    stickyAdd() { if (this.form) this.addToCart(); }

    /* ---------------- Subscription apps (Recurpay) ----------------
       This used to hide our own buy button whenever a subscription was
       selected and hand over to the app's button. That handover was the bug:
       Recurpay's button adds a ONE-TIME line. Verified three times on the live
       store from an empty cart, the last with a real mouse click while our
       button was provably unreachable (`offsetParent === null`) — widget
       showing $38.24, cart receiving $44.99 with `selling_plan_allocation:
       null`, still unrepaired 12s later.

       So the handover is reversed. The widget is treated as what it reliably
       is — a picker — and its selection is mirrored into the native
       `selling_plan` field that Liquid renders on the form. Our button does
       the buying, Recurpay's is hidden, and there is exactly one way to buy.
       Same mechanism as sections/lp-offer, which is verified working. */
    observeSellingPlan() {
      if (!this.form) return;
      var self = this;

      /* Built-in purchase options (the "Purchase options" block): the radios
         are the selling_plan field, the app is stood down in Liquid, and none
         of the Recurpay mirroring below applies. */
      if (this.querySelector('[data-fp-plans]')) {
        this.initNativePlans();
        return;
      }

      var field = this.querySelector('[data-fp-selling-plan]');

      if (!field) {
        /* No field to post (an older stored template). We cannot buy a
           subscription ourselves, so stand down and let the app's button try
           rather than showing a button that would silently buy one-time. */
        var legacy = function () { self.toggleNativeBuy(!self.isSubscriptionSelected()); };
        this.addEventListener('change', legacy);
        this.addEventListener('click', function () { setTimeout(legacy, 0); });
        try {
          new MutationObserver(legacy).observe(this, {
            childList: true, subtree: true, attributes: true,
            attributeFilter: ['value', 'checked', 'class', 'aria-checked']
          });
        } catch (e) {}
        legacy();
        setTimeout(legacy, 400);
        setTimeout(legacy, 1500);
        return;
      }

      /* Our button buys both options now, so it must never be hidden — including
         by an inline display:none left over from a previously stored state. */
      this.toggleNativeBuy(true);

      /* Write only on a real change, and never write anything the observer
         below watches. On an `<input type="hidden">` the `value` IDL property
         is in "default" mode: assigning to it writes the `value` CONTENT
         ATTRIBUTE (a text input does not). With 'value' in the attributeFilter
         that write re-enters the callback, which writes again, and the
         microtask queue never drains — it froze the landing page solid before
         it was found. Hence 'value' is absent from the filter below, and
         nothing is written unless it actually differs. */
      var lastPlan = null;
      var inSync = false;

      var sync = function () {
        if (inSync) return;
        inSync = true;
        try {
          var plan = self.selectedRecurpayPlan();
          if (plan !== lastPlan) {
            lastPlan = plan;
            field.value = plan;
          }
          self.querySelectorAll('[data-recurpay-action]').forEach(function (btn) {
            if (btn.style.display !== 'none') btn.style.display = 'none';
          });
          /* Recurpay (2026-09) tags the theme's own Add to cart and the
             Shop Pay button with .recurpay-hide (display:none !important),
             expecting its button to take over. We hide its button above, so
             without this the page has no way to buy. Class-guarded so the
             'class' observer below settles after one pass. */
          self.querySelectorAll('[data-fp-buy] .recurpay-hide').forEach(function (el) {
            el.classList.remove('recurpay-hide');
          });
        } finally {
          inSync = false;
        }
      };

      // Coalesce bursts: the app mounts its widget in many small mutations.
      var scheduled = false;
      var scheduleSync = function () {
        if (scheduled) return;
        scheduled = true;
        requestAnimationFrame(function () { scheduled = false; sync(); });
      };

      this.addEventListener('change', sync);
      this.addEventListener('click', function () { setTimeout(sync, 0); });
      try {
        new MutationObserver(scheduleSync).observe(this, {
          childList: true, subtree: true, attributes: true,
          attributeFilter: ['class', 'checked', 'selected', 'aria-checked']
        });
      } catch (e) {}

      sync();
      [300, 1000, 2500, 5000].forEach(function (ms) { setTimeout(sync, ms); });

      /* Last read as the form goes: the posted plan always matches the screen.
         Capture phase so it runs before the submit handler builds the body. */
      this.form.addEventListener('submit', sync, true);
      var addBtn = this.querySelector('[data-fp-add-btn]');
      if (addBtn) addBtn.addEventListener('click', sync, true);
    }

    /* Same behaviour as lp-offer.js initPlans + guardSellingPlan. Delegated on
       the element, because fetchSection() swaps the radios' markup on a
       variant change and per-input listeners would be lost. */
    initNativePlans() {
      var self = this;
      // Our button buys both options; never let a stored hide state win.
      this.toggleNativeBuy(true);

      var reflect = function () {
        self.querySelectorAll('[data-fp-plan]').forEach(function (plan) {
          var input = plan.querySelector('.po__input');
          plan.classList.toggle('is-selected', !!input && input.checked);
        });
      };
      this.addEventListener('change', function (e) {
        if (e.target && e.target.classList && e.target.classList.contains('po__input')) reflect();
      });

      /* `?purchase=one-time` opens on the one-time radio. The early access
         page's discount link carries it: promo codes don't apply to
         subscription lines, and the subscription is pre-checked otherwise.
         Visitors without the parameter see the page exactly as before.
         `.checked` sets checkedness, not an attribute, and nothing here
         observes attributes (see the 'value' freeze note above). */
      try {
        if (new URLSearchParams(window.location.search).get('purchase') === 'one-time') {
          var oneTime = this.querySelector('.po__input[value=""]');
          if (oneTime) oneTime.checked = true;
        }
      } catch (e) {}

      // Browsers restore radio state on a soft reload; match the marks to it.
      reflect();

      /* One selling_plan, and it is ours. If the Recurpay embed ever runs here
         despite the stand-down, it appends its own name="selling_plan" input,
         and a hidden input posts exactly like a visible one. Strip anything
         that is not one of our radios as the form goes. Submit-time only — no
         MutationObserver (see the 'value' freeze note above). */
      var strip = function () { self.stripForeignPlans(); };
      this.form.addEventListener('submit', strip, true);
      var addBtn = this.querySelector('[data-fp-add-btn]');
      if (addBtn) addBtn.addEventListener('click', strip, true);
    }

    stripForeignPlans() {
      if (!this.form || !this.querySelector('[data-fp-plans]')) return;
      this.form.querySelectorAll('[name="selling_plan"]').forEach(function (field) {
        if (field.classList.contains('po__input')) return;
        if (field.parentNode) field.parentNode.removeChild(field);
      });
    }

    /* Which plan the Recurpay widget currently has selected, '' for one-time. */
    selectedRecurpayPlan() {
      var widget = this.querySelector('.recurpay-pdp-widget, [data-recurpay-widget]');
      if (!widget) return '';

      var sub = this.querySelector('input[name="recurpay__group_main"][value="subscription"]');
      var container = this.querySelector('.recurpay__widget_container[data-type="subscription-purchase"]');
      var isSub = (sub && sub.checked) ||
                  (container && container.className.indexOf('--selected') !== -1);
      if (!isSub) return '';

      // Which frequency, if the widget offers more than one.
      var select = this.querySelector('select[name="recurpay-input"]');
      if (select) {
        var opt = select.options[select.selectedIndex] || select.options[0];
        if (opt && opt.dataset.sellingPlan) return opt.dataset.sellingPlan;
      }
      var anyOpt = this.querySelector('[data-selling-plan]');
      if (anyOpt) return anyOpt.getAttribute('data-selling-plan') || '';
      // Fall back to the plan Liquid found on the variant.
      var field = this.querySelector('[data-fp-selling-plan]');
      return (field && field.getAttribute('data-default-plan')) || '';
    }

    isSubscriptionSelected() {
      var form = this.form;
      if (!form) return false;
      // 1) Standard Shopify selling_plan input set by most subscription apps.
      var spRadio = form.querySelector('input[name="selling_plan"]:checked, input[name="selling_plan[0][id]"]:checked');
      if (spRadio) return !!spRadio.value;
      var spHidden = form.querySelector('input[name="selling_plan"]:not([type="radio"]), input[name="selling_plan[0][id]"]:not([type="radio"])');
      if (spHidden) return !!spHidden.value;
      // 2) Fallback: Recurpay purchase-type radios, matched by their visible label.
      var radios = form.querySelectorAll('input[type="radio"]:checked, [role="radio"][aria-checked="true"]');
      for (var i = 0; i < radios.length; i++) {
        var r = radios[i];
        if (r.hasAttribute && r.hasAttribute('data-fp-option-input')) continue; // skip our variant inputs
        var t = this.radioLabelText(r);
        if (/one[\s-]?time/i.test(t)) return false;                       // "One-Time Purchase"
        if (/subscribe|subscription|and\s*save|& *save/i.test(t)) return true; // "Subscribe Now and Save"
      }
      return false;
    }

    radioLabelText(r) {
      var t = '';
      try {
        if (r.id) {
          var esc = (window.CSS && CSS.escape) ? CSS.escape(r.id) : r.id;
          var byFor = document.querySelector('label[for="' + esc + '"]');
          if (byFor) t += ' ' + byFor.textContent;
        }
      } catch (e) {}
      var wrap = r.closest ? r.closest('label') : null;
      if (wrap) t += ' ' + wrap.textContent;
      t += ' ' + (r.value || '') + ' ' + ((r.getAttribute && r.getAttribute('aria-label')) || '');
      return t;
    }

    toggleNativeBuy(show) {
      var buy = this.querySelector('[data-fp-buy]');
      if (buy && buy.dataset.fpSubHide !== 'false') buy.style.display = show ? '' : 'none';
      // Keep the sticky bar in sync so it can't add a one-time item during a subscription.
      var stickyAdd = this.querySelector('[data-fp-sticky-add]');
      if (stickyAdd) stickyAdd.style.display = show ? '' : 'none';
    }

    /* ---------------- Sticky bar ---------------- */
    initStickyBar() {
      var bar = this.querySelector('[data-fp-sticky-bar]');
      var buy = this.querySelector('[data-fp-buy]');
      if (!bar || !buy || this.dataset.stickyBar !== 'true' || !('IntersectionObserver' in window)) return;
      bar.hidden = false;
      var io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          var past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          bar.classList.toggle('is-visible', past);
          bar.setAttribute('aria-hidden', past ? 'false' : 'true');
        });
      }, { threshold: 0 });
      io.observe(buy);
    }

    /* ---------------- Tabs ---------------- */
    initTabsDefault() {}
    switchTab(btn) {
      var wrap = btn.closest('[data-fp-tabs]');
      if (!wrap) return;
      var id = btn.getAttribute('data-fp-tab');
      wrap.querySelectorAll('[data-fp-tab]').forEach((b) => b.classList.toggle('is-active', b === btn));
      wrap.querySelectorAll('[data-fp-tab-panel]').forEach((p) => p.classList.toggle('is-active', p.getAttribute('data-fp-tab-panel') === id));
    }

    /* ---------------- Share ---------------- */
    shareProduct(btn) {
      var url = btn.dataset.url || window.location.href;
      var title = btn.dataset.title || document.title;
      if (navigator.share) { navigator.share({ title: title, url: url }).catch(() => {}); return; }
      navigator.clipboard.writeText(url).then(() => this.toast('Link copied')).catch(() => this.toast(url));
    }
    copyLink(btn) {
      var url = btn.dataset.url || window.location.href;
      if (navigator.share) { navigator.share({ url: url }).catch(() => {}); return; }
      navigator.clipboard.writeText(url).then(() => this.toast('Link copied')).catch(() => this.toast(url));
    }

    /* ---------------- Notify me ---------------- */
    toggleNotify() {
      var f = this.querySelector('[data-fp-notify-form]');
      if (f) f.hidden = !f.hidden;
    }
    submitNotify() {
      var email = this.querySelector('[data-fp-notify-email]');
      if (!email || !email.value || email.value.indexOf('@') === -1) { this.toast('Enter a valid email'); return; }
      // Integration hook: connect to Klaviyo/back-in-stock app here.
      document.dispatchEvent(new CustomEvent('cp:notify:request', { detail: { email: email.value, variantId: this.idInput && this.idInput.value } }));
      this.toast('We will email you when it is back');
      var f = this.querySelector('[data-fp-notify-form]'); if (f) f.hidden = true;
    }

    /* ---------------- Countdown ---------------- */
    initCountdowns() {
      this.querySelectorAll('[data-fp-countdown]').forEach((el) => {
        var end = new Date((el.dataset.end || '').replace(' ', 'T')).getTime();
        var clock = el.querySelector('[data-fp-countdown-clock]');
        if (!end || !clock) return;
        var tick = function () {
          var diff = end - Date.now();
          if (diff <= 0) { clock.textContent = '00:00:00'; clearInterval(timer); return; }
          var d = Math.floor(diff / 86400000);
          var h = Math.floor((diff % 86400000) / 3600000);
          var m = Math.floor((diff % 3600000) / 60000);
          var s = Math.floor((diff % 60000) / 1000);
          clock.textContent = (d > 0 ? d + 'd ' : '') + String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0') + ':' + String(s).padStart(2, '0');
        };
        tick();
        var timer = setInterval(tick, 1000);
      });
    }

    /* ---------------- "People viewing" ---------------- */
    initViewing() {
      this.querySelectorAll('[data-fp-viewing]').forEach((el) => {
        var base = parseInt(el.textContent, 10) || 10;
        setInterval(() => {
          var n = base + Math.floor(Math.random() * 6) - 2;
          el.textContent = Math.max(2, n);
        }, 8000);
      });
    }

    /* ---------------- Animations ---------------- */
    initAnimations() {
      if (!this.classList.contains('fp--animate') || REDUCED || !('IntersectionObserver' in window)) {
        this.querySelectorAll('.fp__anim').forEach((el) => el.classList.add('is-visible'));
        return;
      }
      var io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => { if (entry.isIntersecting) { entry.target.classList.add('is-visible'); io.unobserve(entry.target); } });
      }, { threshold: 0.1 });
      this.querySelectorAll('.fp__anim').forEach((el) => io.observe(el));
    }

    /* ---------------- Product cards ---------------- */
    card(p) {
      var img = p.featured_image || (p.images && p.images[0]) || (p.featured_media && p.featured_media.preview_image && p.featured_media.preview_image.src) || '';
      var price = typeof p.price === 'number' ? p.price : parseInt(p.price, 10);
      var a = document.createElement('a');
      a.className = 'fp__reco-card';
      a.href = p.url;
      a.innerHTML =
        (img ? '<img loading="lazy" src="' + upscale(img, 400) + '" alt="">' : '') +
        '<div class="fp__reco-card-title">' + (p.title || '') + '</div>' +
        '<div class="fp__reco-card-price">' + formatMoney(price) + '</div>';
      return a;
    }

    initComplementary() {
      var wrap = this.querySelector('[data-fp-complementary]');
      if (!wrap) return;
      var list = wrap.querySelector('[data-fp-reco-list]');
      var id = wrap.dataset.productId;
      var limit = wrap.dataset.limit || 3;
      fetch('/recommendations/products.json?product_id=' + id + '&intent=complementary&limit=' + limit)
        .then((r) => r.json())
        .then((data) => {
          if (!data.products || !data.products.length) { wrap.hidden = true; return; }
          data.products.forEach((p) => list.appendChild(this.card(p)));
        })
        .catch(() => { wrap.hidden = true; });
    }

    initRecentlyViewed() {
      var KEY = 'fp-recently-viewed';
      var current = handleFromUrl(this.productUrl);
      var list = [];
      try { list = JSON.parse(localStorage.getItem(KEY)) || []; } catch (e) {}

      var wrap = this.querySelector('[data-fp-recent]');
      if (wrap) {
        var container = wrap.querySelector('[data-fp-reco-list]');
        var limit = parseInt(wrap.dataset.limit, 10) || 4;
        var handles = list.filter((h) => h !== current).slice(0, limit);
        if (!handles.length) { wrap.hidden = true; }
        else {
          Promise.all(handles.map((h) => fetch('/products/' + h + '.js').then((r) => r.ok ? r.json() : null).catch(() => null)))
            .then((products) => {
              var any = false;
              products.forEach((p) => { if (p) { container.appendChild(this.card(p)); any = true; } });
              if (!any) wrap.hidden = true;
            });
        }
      }

      // Record current product for next time.
      if (current) {
        list = list.filter((h) => h !== current);
        list.unshift(current);
        list = list.slice(0, 12);
        try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (e) {}
      }
    }

    /* ---------------- Helpers ---------------- */
    toast(msg) {
      var el = this.querySelector('[data-fp-toast]');
      if (!el) return;
      el.textContent = msg;
      el.hidden = false;
      requestAnimationFrame(() => el.classList.add('is-visible'));
      clearTimeout(this._toastT);
      this._toastT = setTimeout(() => {
        el.classList.remove('is-visible');
        setTimeout(() => { el.hidden = true; }, 250);
      }, 2600);
    }

    announce(msg) {
      var el = this.querySelector('[data-fp-toast]');
      if (el) el.setAttribute('aria-label', msg);
    }
  }

  customElements.define('featured-product', FeaturedProduct);
})();
